import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, DollarSign, ArrowRight, CheckCircle2, 
  FileText, HelpCircle, TrendingUp, Globe, AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import CryptoTaxCalculatorTool from '../components/tools/stage1/CryptoTaxCalculatorTool';
import { TaxPageMonetization, TaxReportEmailCapture } from '../components/TaxPageMonetization';

const CryptoTaxCalculatorUSA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How is cryptocurrency taxed in the USA?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In the United States, cryptocurrency is treated as property by the IRS. Capital gains tax applies when you sell, trade, or spend crypto. Short-term gains (held less than 1 year) are taxed at ordinary income rates (10-37%), while long-term gains (held over 1 year) are taxed at preferential rates of 0%, 15%, or 20% depending on your taxable income."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to report crypto on my US tax return?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, the IRS requires you to report all cryptocurrency transactions. Form 8949 is used to report capital gains and losses from crypto sales. The IRS now includes a specific question about virtual currency on Form 1040. Failure to report can result in penalties and interest."
        }
      },
      {
        "@type": "Question",
        "name": "What is the crypto tax rate in the USA for 2026?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For 2026, short-term crypto gains are taxed at your ordinary income tax rate (10% to 37%). Long-term capital gains rates are 0% for income up to $47,025 (single), 15% for income up to $518,900, and 20% for income above that threshold."
        }
      },
      {
        "@type": "Question",
        "name": "Can I deduct crypto losses on my US taxes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, you can deduct capital losses from cryptocurrency against capital gains. If your losses exceed your gains, you can deduct up to $3,000 ($1,500 if married filing separately) against ordinary income. Remaining losses can be carried forward to future tax years indefinitely."
        }
      },
      {
        "@type": "Question",
        "name": "Is crypto-to-crypto trading taxable in the USA?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, swapping one cryptocurrency for another is a taxable event in the USA. For example, trading Bitcoin for Ethereum triggers capital gains tax based on the fair market value at the time of the trade. Each trade must be tracked and reported."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Crypto Tax Calculator USA (2026) - Free IRS Capital Gains Calculator"
        description="Calculate your US cryptocurrency taxes free. Estimate federal capital gains tax on Bitcoin, Ethereum trades. Updated for 2026 IRS rates. Short-term vs long-term gains explained."
        keywords="crypto tax calculator USA, US cryptocurrency tax, IRS crypto tax 2026, Bitcoin tax calculator, capital gains tax crypto, crypto tax rate USA"
        canonical="https://toolsforgeapp.com/crypto-tax-calculator-usa"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen py-12 px-6" data-testid="crypto-tax-usa-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/crypto-calculators" className="hover:text-primary transition-colors">Crypto Calculators</Link>
            <span>/</span>
            <span className="text-foreground">USA Tax Calculator</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-blue-500/30 text-blue-400">
              <Globe className="w-3 h-3 mr-2" />
              Updated for 2026 IRS Rates
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Crypto Tax Calculator USA
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Calculate your US federal cryptocurrency capital gains tax instantly. Our free calculator estimates your tax liability on Bitcoin, Ethereum, and all crypto trades based on 2026 IRS guidelines. Whether you're dealing with short-term or long-term holdings, get accurate estimates in seconds.
            </p>
          </motion.div>

          {/* Key Info Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="p-4 rounded-xl border border-blue-500/30 bg-blue-500/10">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-400" />
                <span className="font-semibold">Short-Term Rate</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">10-37%</p>
              <p className="text-xs text-muted-foreground">Holdings under 1 year</p>
            </div>
            <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/10">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Long-Term Rate</span>
              </div>
              <p className="text-2xl font-bold text-green-400">0-20%</p>
              <p className="text-xs text-muted-foreground">Holdings over 1 year</p>
            </div>
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="font-semibold">Loss Deduction</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">$3,000</p>
              <p className="text-xs text-muted-foreground">Max annual deduction</p>
            </div>
          </div>

          {/* Calculator Tool */}
          <div className="mb-8">
            <CryptoTaxCalculatorTool />
          </div>

          {/* Monetization Section - After Calculator */}
          <TaxPageMonetization 
            region="usa"
            complianceText="Ensure IRS-compliant Form 8949 reporting"
          />

          {/* How to Calculate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Calculator className="w-6 h-6 text-primary" />
              How to Calculate Crypto Taxes in the USA
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground mb-4">
                The IRS treats cryptocurrency as property, meaning every sale, trade, or exchange is a taxable event. Here's how to calculate your crypto tax liability:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">1</span>
                    Determine Your Cost Basis
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your cost basis is the original purchase price plus any fees paid. For example, if you bought 1 BTC for $40,000 with a $100 exchange fee, your cost basis is $40,100.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">2</span>
                    Calculate Your Gain or Loss
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Subtract your cost basis from the sale price. If you sold that 1 BTC for $65,000, your capital gain is $65,000 - $40,100 = $24,900.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">3</span>
                    Determine Holding Period
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Short-term (under 1 year) gains are taxed at ordinary income rates. Long-term (over 1 year) gains qualify for preferential capital gains rates.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">4</span>
                    Apply the Tax Rate
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Multiply your gain by the applicable tax rate. For long-term gains in the 15% bracket: $24,900 × 15% = $3,735 tax owed.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Example Calculation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 p-6 rounded-xl border border-border bg-card"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Example: US Crypto Tax Calculation
            </h2>
            
            <div className="bg-gray-900/50 rounded-lg p-4 font-mono text-sm">
              <p className="text-muted-foreground mb-2">// Scenario: Sold Bitcoin after holding for 2 years</p>
              <p className="text-blue-400">Purchase Price: $30,000</p>
              <p className="text-blue-400">Sale Price: $75,000</p>
              <p className="text-blue-400">Quantity: 0.5 BTC</p>
              <p className="text-muted-foreground mt-2">// Calculation</p>
              <p className="text-white">Cost Basis: 0.5 × $30,000 = <span className="text-yellow-400">$15,000</span></p>
              <p className="text-white">Sale Proceeds: 0.5 × $75,000 = <span className="text-yellow-400">$37,500</span></p>
              <p className="text-white">Capital Gain: $37,500 - $15,000 = <span className="text-green-400">$22,500</span></p>
              <p className="text-white">Tax Rate (Long-term, 15% bracket): <span className="text-yellow-400">15%</span></p>
              <p className="text-white mt-2 text-lg">Estimated Tax Owed: $22,500 × 15% = <span className="text-red-400 font-bold">$3,375</span></p>
            </div>
          </motion.section>

          {/* Email Capture - Before FAQ */}
          <TaxReportEmailCapture region="usa" />

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How is cryptocurrency taxed in the USA?",
                  a: "The IRS treats cryptocurrency as property. You pay capital gains tax when you sell, trade, or spend crypto. Short-term gains (held under 1 year) are taxed at your ordinary income rate (10-37%). Long-term gains (held over 1 year) are taxed at 0%, 15%, or 20% based on your income level."
                },
                {
                  q: "Do I need to report crypto on my US tax return?",
                  a: "Yes, all cryptocurrency transactions must be reported to the IRS. Form 8949 is used for capital gains and losses. The IRS now asks a direct question about virtual currency on Form 1040. Failure to report can result in penalties, interest, and potential criminal charges."
                },
                {
                  q: "What is the crypto tax rate in the USA for 2026?",
                  a: "For 2026, short-term gains are taxed at 10-37% (ordinary income rates). Long-term capital gains rates are 0% for taxable income up to $47,025 (single filers), 15% up to $518,900, and 20% above that. An additional 3.8% Net Investment Income Tax may apply for high earners."
                },
                {
                  q: "Can I deduct crypto losses on my US taxes?",
                  a: "Yes, capital losses from crypto can offset capital gains. If losses exceed gains, you can deduct up to $3,000 per year against ordinary income. Unused losses carry forward indefinitely to future tax years."
                },
                {
                  q: "Is crypto-to-crypto trading taxable in the USA?",
                  a: "Yes, every crypto-to-crypto trade is a taxable event. Trading Bitcoin for Ethereum triggers capital gains tax based on the fair market value at the time of trade. You must track and report each transaction."
                }
              ].map((faq, index) => (
                <div key={index} className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Related Tools */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">Related Crypto Tools</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Crypto Profit Calculator', slug: 'crypto-profit-calculator' },
                { name: 'Bitcoin Calculator', slug: 'bitcoin-profit-calculator' },
                { name: 'Crypto ROI Calculator', slug: 'crypto-roi-calculator' },
                { name: 'Crypto Gain Calculator', slug: 'crypto-gain-calculator' }
              ].map(tool => (
                <Link
                  key={tool.slug}
                  to={`/tools/${tool.slug}`}
                  className="p-3 rounded-lg border border-border bg-card hover:border-primary/50 transition-all text-sm font-medium flex items-center justify-between"
                >
                  {tool.name}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default CryptoTaxCalculatorUSA;
